import { DrayratesRatingMethod, RateMetadata } from '@shared/types/quote';
import { z } from 'zod';

const DrayratesRateRangeSchema = z.object({
  low: z.number(),
  high: z.number(),
});

const DrayratesSuggestedSellRateSchema = z.object({
  competitive: z.number().optional().nullable(),
  recommended: z.number().optional().nullable(),
  aggressive: z.number().optional().nullable(),
});

const DrayratesAccessorialSchema = z.object({
  name: z.string(),
  frequency: z.string().optional().nullable(),
  org: z
    .object({
      rate: z.number(),
    })
    .optional()
    .nullable(),
  market: z
    .object({
      rate: z.number().optional().nullable(),
    })
    .optional()
    .nullable(),
});

const DrayratesResolvedOriginSchema = z.object({
  port: z.string().optional().nullable(),
  metro: z.string().optional().nullable(),
});

const DrayratesRateResponseSchema = z.object({
  selectedRateKey: z.enum(['market', 'buy', 'sell']),
  meta: z
    .object({
      resolvedOrigin: DrayratesResolvedOriginSchema.optional().nullable(),
    })
    .optional()
    .nullable(),
  market: z
    .object({
      rateRange: DrayratesRateRangeSchema.optional().nullable(),
    })
    .optional()
    .nullable(),
  buy: z
    .object({
      suggestedBuyRate: z.number().optional().nullable(),
      buyRateRange: DrayratesRateRangeSchema.optional().nullable(),
      buyStrategy: z.string().optional().nullable(),
    })
    .optional()
    .nullable(),
  sell: z
    .object({
      sellStrategy: z.string().optional().nullable(),
      suggestedSellRate: DrayratesSuggestedSellRateSchema.optional().nullable(),
    })
    .optional()
    .nullable(),
  accessorials: z.array(DrayratesAccessorialSchema).optional().nullable(),
});

type DrayratesAccessorial = z.infer<typeof DrayratesAccessorialSchema>;
export type DrayratesRateResponse = z.infer<typeof DrayratesRateResponseSchema>;

type HasDrayratesMetadata = {
  metadata?: RateMetadata | null;
};

export const getDrayratesCarrierRatingMethod = (
  metadata?: RateMetadata | null
): DrayratesRatingMethod | null => {
  if (
    metadata?.ratingMethod === DrayratesRatingMethod.MARKET ||
    metadata?.ratingMethod === DrayratesRatingMethod.BUY
  ) {
    return metadata.ratingMethod;
  }

  if (metadata?.key === DrayratesRatingMethod.MARKET) {
    return DrayratesRatingMethod.MARKET;
  }

  if (metadata?.key === DrayratesRatingMethod.BUY) {
    return DrayratesRatingMethod.BUY;
  }

  return null;
};

export const getDefaultDrayratesRatingMethod = (
  configuredRatingMethod?: string | null
): DrayratesRatingMethod => {
  return configuredRatingMethod === DrayratesRatingMethod.MARKET
    ? DrayratesRatingMethod.MARKET
    : DrayratesRatingMethod.BUY;
};

export const selectDefaultDrayratesEntry = <
  EntryT extends HasDrayratesMetadata,
>(
  entries: EntryT[],
  configuredRatingMethod?: string | null
): EntryT | undefined => {
  const defaultRatingMethod = getDefaultDrayratesRatingMethod(
    configuredRatingMethod
  );
  const fallbackRatingMethod =
    defaultRatingMethod === DrayratesRatingMethod.BUY
      ? DrayratesRatingMethod.MARKET
      : DrayratesRatingMethod.BUY;

  return (
    entries.find(
      (entry) =>
        getDrayratesCarrierRatingMethod(entry.metadata) === defaultRatingMethod
    ) ||
    entries.find(
      (entry) =>
        getDrayratesCarrierRatingMethod(entry.metadata) === fallbackRatingMethod
    ) ||
    entries[0]
  );
};

export const parseDrayratesRateResponse = (
  metadata?: RateMetadata
): DrayratesRateResponse | null => {
  const parsedResponse = DrayratesRateResponseSchema.safeParse(
    metadata?.response
  );

  if (!parsedResponse.success) {
    return null;
  }

  return parsedResponse.data;
};

export const getDrayratesSelectedRateRange = (
  response?: DrayratesRateResponse | null
): { low: number; high: number } | null => {
  if (!response) {
    return null;
  }

  switch (response.selectedRateKey) {
    case 'market':
      return response.market?.rateRange ?? null;
    case 'buy':
      return response.buy?.buyRateRange ?? null;
    case 'sell': {
      const low = response.sell?.suggestedSellRate?.competitive;
      const high = response.sell?.suggestedSellRate?.aggressive;

      if (low == null || high == null) {
        return null;
      }

      return { low, high };
    }
    default:
      return null;
  }
};

export const getDrayratesSelectedRateStrategy = (
  response?: DrayratesRateResponse | null
): { label: string; value: string } | null => {
  if (!response) {
    return null;
  }

  switch (response.selectedRateKey) {
    case 'buy':
      return response.buy?.buyStrategy
        ? {
            label: 'Buy strategy',
            value: response.buy.buyStrategy,
          }
        : null;
    case 'sell':
      return response.sell?.sellStrategy
        ? {
            label: 'Sell strategy',
            value: response.sell.sellStrategy,
          }
        : null;
    default:
      return null;
  }
};

export const getDrayratesWidgetStrategies = (
  response?: DrayratesRateResponse | null
): Array<{ label: string; value: string }> => {
  if (!response) {
    return [];
  }

  return [
    response.buy?.buyStrategy
      ? {
          label: 'Buy strategy',
          value: response.buy.buyStrategy,
        }
      : null,
    response.sell?.sellStrategy
      ? {
          label: 'Sell strategy',
          value: response.sell.sellStrategy,
        }
      : null,
  ].filter((value): value is { label: string; value: string } => value != null);
};

export const getDrayratesResolvedTerminalDisplay = (
  response?: DrayratesRateResponse | null
): string | null => {
  const port = response?.meta?.resolvedOrigin?.port;
  const metro = response?.meta?.resolvedOrigin?.metro;

  if (!port) {
    return null;
  }

  return metro ? `${port} (${metro})` : port;
};

export const getDrayratesSelectedRateValue = (
  response?: DrayratesRateResponse | null
): number | null => {
  if (!response) {
    return null;
  }

  switch (response.selectedRateKey) {
    case 'market':
      return null;
    case 'buy':
      return response.buy?.suggestedBuyRate ?? null;
    case 'sell':
      return response.sell?.suggestedSellRate?.recommended ?? null;
    default:
      return null;
  }
};

const getAccessorialDisplayedRate = (
  accessorial: DrayratesAccessorial
): number => {
  return accessorial.org?.rate ?? accessorial.market?.rate ?? -1;
};

const formatAccessorialSummaryItem = (
  accessorial: DrayratesAccessorial
): string => {
  const rate = accessorial.org?.rate ?? accessorial.market?.rate;
  const rateDisplay = rate == null ? '' : ` $${rate}`;
  const frequencyDisplay = accessorial.frequency
    ? `/${accessorial.frequency}`
    : '';

  return `${accessorial.name}${rateDisplay}${frequencyDisplay}`;
};

export const getDrayratesAccessorialSummary = (
  response?: DrayratesRateResponse | null
): string | null => {
  if (!response?.accessorials?.length) {
    return null;
  }

  const previewItems = [...response.accessorials]
    .sort(
      (left, right) =>
        getAccessorialDisplayedRate(right) - getAccessorialDisplayedRate(left)
    )
    .slice(0, 3)
    .map(formatAccessorialSummaryItem)
    .join(', ');
  const remainingCount = response.accessorials.length - 3;
  const remainingDisplay =
    remainingCount > 0 ? `, +${remainingCount} more` : '';

  return `${response.accessorials.length} accessorial${
    response.accessorials.length === 1 ? '' : 's'
  }: ${previewItems}${remainingDisplay}`;
};
